/* ==========================================================================
   DUPE OUTDOORS — product page behaviour (gallery, variant picker, quantity)

   WHY THIS FILE EXISTS: these three lived in assets/dupeoutdoors.js alongside
   the old cream design system. Retiring that system in v14 removed its script
   tag from layout/theme.liquid — and silently took the variant picker with
   it. The colour pills still rendered, still looked tappable, and did nothing:
   the cart's hidden variant id stayed on the first variant, so every order
   would have been that colour. Caught by Sam tapping Navy on the beanie.

   Ported here, scoped to product pages, so the retired file never has to be
   reloaded (it also runs its own [data-reveal] observer, which would fight
   dupe-field.js). Loaded from sections/main-product.liquid.

   Markup contract (sections/main-product.liquid):
     .do-pdp-wrap                         one per product section
       [data-do-gallery]                  gallery root
         [data-gallery-main] img          the large image
         [data-gallery-thumb]             thumbs; data-src / data-srcset / data-alt
       form[data-type="add-to-cart-form"]
         script[type="application/json"]  {{ product.variants | json }}
         input[name="id"]                 the variant the cart receives
         .do-variant-pill                 data-option-position / data-option-value
         [name="add"]                     add-to-cart button
       .do-qty  [data-do-dec] .do-qty__input [data-do-inc]
   ========================================================================== */
(function () {
  'use strict';

  function money(cents) { return '$' + (cents / 100).toFixed(2); }

  /* ---------- gallery ---------- */
  function Gallery(el) {
    this.el = el;
    this.mainEl = el.querySelector('[data-gallery-main]');
    this.thumbs = Array.prototype.slice.call(el.querySelectorAll('[data-gallery-thumb]'));
    el._gallery = this;
    var self = this;
    this.thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { self.select(i); });
      thumb.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); self.select(i); }
      });
    });
  }
  Gallery.prototype.select = function (i) {
    var thumb = this.thumbs[i];
    if (!thumb || !this.mainEl) return;
    var img = this.mainEl.querySelector('img');
    if (img && thumb.dataset.src) {
      img.src = thumb.dataset.src;
      if (thumb.dataset.srcset) img.srcset = thumb.dataset.srcset;
      img.alt = thumb.dataset.alt || '';
      img.style.display = '';
    }
    var noPhoto = this.mainEl.querySelector('.do-gallery__no-photo');
    if (noPhoto) noPhoto.style.display = 'none';
    this.thumbs.forEach(function (t) { t.classList.remove('is-active'); });
    thumb.classList.add('is-active');
  };

  /* ---------- variant picker ---------- */
  function Picker(wrap) {
    this.wrap = wrap;
    this.form = wrap.querySelector('form[data-type="add-to-cart-form"]');
    if (!this.form) return;
    var json = this.form.querySelector('script[type="application/json"]');
    if (!json) return;
    try { this.variants = JSON.parse(json.textContent); } catch (e) { return; }

    this.idInput = this.form.querySelector('input[name="id"]');
    this.atcBtn = this.form.querySelector('[name="add"]');
    this.atcLabel = this.atcBtn ? this.atcBtn.textContent.trim() : 'Add to cart';
    this.priceEl = wrap.querySelector('[data-brand-price]');
    this.brandPrice = parseInt((this.priceEl && this.priceEl.dataset.brandPrice) || '0', 10);
    this.galleryMain = wrap.querySelector('[data-gallery-main]');
    var g = wrap.querySelector('[data-do-gallery]');
    this.gallery = (g && g._gallery) || null;

    var self = this;
    this.selected = {};
    wrap.querySelectorAll('.do-variant-pill.is-selected').forEach(function (b) {
      self.selected[b.dataset.optionPosition] = b.dataset.optionValue;
    });
    wrap.querySelectorAll('.do-variant-pill').forEach(function (b) {
      b.addEventListener('click', function () { self.onPill(b); });
    });
  }

  Picker.prototype.onPill = function (btn) {
    var pos = btn.dataset.optionPosition;
    var val = btn.dataset.optionValue;
    var group = btn.closest('.do-variant-pills');
    if (group) {
      group.querySelectorAll('.do-variant-pill').forEach(function (b) {
        b.classList.remove('is-selected');
        b.setAttribute('aria-checked', 'false');
      });
    }
    btn.classList.add('is-selected');
    btn.setAttribute('aria-checked', 'true');

    var outer = btn.closest('.do-variant-group');
    var em = outer && outer.querySelector('em');
    if (em) em.textContent = val;

    this.selected[pos] = val;
    var v = this.find();
    if (!v) { this.unavailable(); return; }

    if (this.idInput) this.idInput.value = v.id;
    this.price(v);
    this.atc(v);
    this.image(v);
    // Keep the URL on the chosen variant so a shared link opens on that colour.
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url.toString());
    } catch (e) { /* no-op */ }
  };

  Picker.prototype.find = function () {
    var sel = this.selected;
    return this.variants.filter(function (v) {
      return Object.keys(sel).every(function (p) { return v['option' + p] === sel[p]; });
    })[0];
  };

  Picker.prototype.unavailable = function () {
    if (!this.atcBtn) return;
    this.atcBtn.disabled = true;
    this.atcBtn.textContent = 'Unavailable';
  };

  Picker.prototype.price = function (v) {
    if (!this.priceEl) return;
    var ours = this.priceEl.querySelector('.receipt__our-price');
    var save = this.priceEl.querySelector('.receipt__savings');
    var brand = this.priceEl.querySelector('.receipt__brand-price');
    var row = this.priceEl.querySelector('.receipt__brand-row');
    var bp = this.brandPrice > 0 ? this.brandPrice : (v.compare_at_price || 0);
    if (ours) ours.textContent = money(v.price);
    if (brand && bp > 0) brand.textContent = money(bp);
    if (row) row.style.display = bp > v.price ? '' : 'none';
    if (save) {
      if (bp > v.price) { save.textContent = 'SAVE ' + money(bp - v.price); save.style.display = ''; }
      else { save.style.display = 'none'; }
    }
  };

  Picker.prototype.atc = function (v) {
    if (!this.atcBtn) return;
    this.atcBtn.disabled = !v.available;
    this.atcBtn.textContent = v.available ? this.atcLabel : 'Sold out';
  };

  Picker.prototype.image = function (v) {
    var fi = v.featured_image;
    if (!fi || !fi.src) return;
    var base = fi.src.split('?')[0];
    if (this.gallery && this.gallery.thumbs.length) {
      var idx = -1;
      this.gallery.thumbs.some(function (t, i) {
        if ((t.dataset.src || '').split('?')[0] === base) { idx = i; return true; }
        return false;
      });
      if (idx >= 0) { this.gallery.select(idx); return; }
    }
    if (!this.galleryMain) return;
    var img = this.galleryMain.querySelector('img');
    if (img) { img.src = base + '?width=900'; img.removeAttribute('srcset'); img.alt = fi.alt || ''; }
  };

  /* ---------- quantity stepper ---------- */
  function initQty() {
    document.querySelectorAll('.do-qty').forEach(function (wrap) {
      var input = wrap.querySelector('.do-qty__input');
      if (!input) return;
      var dec = wrap.querySelector('[data-do-dec]');
      var inc = wrap.querySelector('[data-do-inc]');
      if (dec) dec.addEventListener('click', function () {
        var v = parseInt(input.value, 10) || 1;
        if (v > (parseInt(input.min, 10) || 1)) {
          input.value = v - 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      if (inc) inc.addEventListener('click', function () {
        var v = parseInt(input.value, 10) || 1;
        var max = input.max ? parseInt(input.max, 10) : Infinity;
        if (v < max) {
          input.value = v + 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }

  function boot() {
    // Gallery first: the picker reads el._gallery off the initialised root.
    document.querySelectorAll('[data-do-gallery]').forEach(function (el) { new Gallery(el); });
    document.querySelectorAll('.do-pdp-wrap').forEach(function (w) { new Picker(w); });
    initQty();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
